import { CompanySpaceOrganizationUnitIndexResponse } from '../generated-api';

export type WorkplaceUnit = CompanySpaceOrganizationUnitIndexResponse;

export type WorkplaceAssignment = NonNullable<WorkplaceUnit['organization_unit_leaders']>[number];

export interface WorkplaceNode {
  id: number;
  unit: WorkplaceUnit;
  parentId: number | null;
  level: number;
  children: WorkplaceNode[];
  /** memberships in this unit alone, as reported by the API */
  userCount: number;
  /** distinct people with a membership in this unit or anywhere below it */
  totalUserCount: number;
}

export interface WorkplaceTree {
  roots: WorkplaceNode[];
  byId: Map<number, WorkplaceNode>;
}

export interface WorkplaceLeadership {
  /**
   * A post can be held by several people at once (rozhodnutí C1, 4. 9. 2026): the
   * workflow lets the fastest of them decide, or the submitter names one. The stand-in
   * is still at most one -- they take over for the whole set.
   */
  leaders: WorkplaceAssignment[];
  deputy?: WorkplaceAssignment;
}

export interface WorkplaceRow {
  node: WorkplaceNode;
  level: number;
  hasChildren: boolean;
  isExpanded: boolean;
  /** matched the active search/filter itself, as opposed to being shown as a parent of a match */
  isMatch: boolean;
  posInSet: number;
  setSize: number;
}

const compareSiblings = (a: WorkplaceNode, b: WorkplaceNode): number => {
  const posA = a.unit.position ?? Number.MAX_SAFE_INTEGER;
  const posB = b.unit.position ?? Number.MAX_SAFE_INTEGER;
  return posA !== posB ? posA - posB : a.unit.name.localeCompare(b.unit.name);
};

export const buildWorkplaceTree = (units: WorkplaceUnit[]): WorkplaceTree => {
  const byId = new Map<number, WorkplaceNode>();
  units.forEach((unit) =>
    byId.set(unit.id, {
      id: unit.id,
      unit,
      parentId: unit.parent_id ?? null,
      level: 0,
      children: [],
      userCount: unit.user_count ?? 0,
      totalUserCount: 0,
    })
  );

  const roots: WorkplaceNode[] = [];
  byId.forEach((node) => {
    const parent = node.parentId !== null ? byId.get(node.parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      // a unit whose parent is not in the payload would drop out of the tree otherwise
      node.parentId = null;
      roots.push(node);
    }
  });

  const finalize = (nodes: WorkplaceNode[], level: number): number => {
    nodes.sort(compareSiblings);
    let total = 0;
    nodes.forEach((node) => {
      node.level = level;
      const summed = node.userCount + finalize(node.children, level + 1);
      // the server counts each person once; the plain sum only stands in for an older API
      node.totalUserCount = node.unit.subtree_user_count ?? summed;
      total += summed;
    });
    return total;
  };
  finalize(roots, 0);

  return { roots, byId };
};

export const ancestorIdsOf = (tree: WorkplaceTree, id: number): number[] => {
  const result: number[] = [];
  const seen = new Set<number>([id]);
  let parentId = tree.byId.get(id)?.parentId ?? null;
  while (parentId !== null && !seen.has(parentId)) {
    seen.add(parentId);
    result.push(parentId);
    parentId = tree.byId.get(parentId)?.parentId ?? null;
  }
  return result;
};

/** names of all ancestors plus the unit itself, root first */
export const pathOf = (tree: WorkplaceTree, id: number): string[] => {
  const names = ancestorIdsOf(tree, id)
    .map((ancestorId) => tree.byId.get(ancestorId)?.unit.name ?? '')
    .reverse();
  const self = tree.byId.get(id)?.unit.name;
  return self ? [...names, self] : names;
};

export const descendantIdsOf = (node: WorkplaceNode): number[] => {
  const result: number[] = [];
  const queue = [...node.children];
  while (queue.length) {
    const current = queue.shift() as WorkplaceNode;
    result.push(current.id);
    queue.push(...current.children);
  }
  return result;
};

export const expandableIdsOf = (tree: WorkplaceTree): number[] => {
  const result: number[] = [];
  tree.byId.forEach((node) => {
    if (node.children.length) {
      result.push(node.id);
    }
  });
  return result;
};

/** matched units plus every ancestor, so the matches stay reachable in the tree */
export const visibleIdsFor = (tree: WorkplaceTree, matchedIds: number[]): Set<number> => {
  const visible = new Set<number>();
  matchedIds.forEach((id) => {
    if (!tree.byId.has(id)) {
      return;
    }
    visible.add(id);
    ancestorIdsOf(tree, id).forEach((ancestorId) => visible.add(ancestorId));
  });
  return visible;
};

export interface FlattenOptions {
  expandedIds: Set<number>;
  /** null means no active filter; otherwise only these units are rendered */
  visibleIds: Set<number> | null;
  matchedIds: Set<number> | null;
}

export const flattenTree = (tree: WorkplaceTree, options: FlattenOptions): WorkplaceRow[] => {
  const { expandedIds, visibleIds, matchedIds } = options;
  const rows: WorkplaceRow[] = [];

  const walk = (nodes: WorkplaceNode[], level: number) => {
    const shown = visibleIds ? nodes.filter((node) => visibleIds.has(node.id)) : nodes;
    shown.forEach((node, index) => {
      const children = visibleIds
        ? node.children.filter((child) => visibleIds.has(child.id))
        : node.children;
      // a filtered tree is shown fully expanded, otherwise matches could stay hidden
      const isExpanded = !!visibleIds || expandedIds.has(node.id);
      rows.push({
        node,
        level,
        hasChildren: children.length > 0,
        isExpanded: children.length > 0 && isExpanded,
        isMatch: !matchedIds || matchedIds.has(node.id),
        posInSet: index + 1,
        setSize: shown.length,
      });
      if (children.length && isExpanded) {
        walk(children, level + 1);
      }
    });
  };
  walk(tree.roots, 0);

  return rows;
};

const pickAssignments = (assignments?: WorkplaceAssignment[]): WorkplaceLeadership => ({
  leaders: assignments?.filter((item) => item.role === 'leader') || [],
  deputy: assignments?.find((item) => item.role === 'deputy'),
});

/** the API sends only currently valid assignments, so no date filtering is needed here */
export const leadershipOf = (unit: WorkplaceUnit): WorkplaceLeadership =>
  pickAssignments(unit.organization_unit_leaders);

export const financialApproversOf = (unit: WorkplaceUnit): WorkplaceLeadership =>
  pickAssignments(unit.organization_unit_financial_approvers);

export interface WorkplaceOption {
  id: number;
  label: string;
  /** the ancestors, so typing a parent name narrows the list down to its children */
  path: string;
}

/**
 * Every workplace the given one may be handed to -- as a new parent, or as somewhere to
 * put its members. Its own subtree is out: a unit cannot end up under itself.
 */
export const targetOptionsFor = (
  tree: WorkplaceTree,
  node: WorkplaceNode
): WorkplaceOption[] => {
  const forbidden = new Set<number>([node.id, ...descendantIdsOf(node)]);
  const options: WorkplaceOption[] = [];
  tree.byId.forEach((candidate) => {
    if (forbidden.has(candidate.id)) {
      return;
    }
    options.push({
      id: candidate.id,
      label: (candidate.unit.code ? candidate.unit.code + ' · ' : '') + candidate.unit.name,
      path: pathOf(tree, candidate.id).slice(0, -1).join(' / '),
    });
  });
  return options;
};

/** "Jan Novák" -> "J. Novák", to keep tree rows on one line */
export const shortName = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) {
    return fullName;
  }
  return `${parts[0].charAt(0)}. ${parts.slice(1).join(' ')}`;
};

export type DropInstruction = 'above' | 'below' | 'inside';

export interface WorkplaceMove {
  parentId: number | null;
  /** 1-based, the way acts_as_list counts */
  position: number;
}

/** how much of the row height each reordering edge takes, capped for short rows */
const EDGE_RATIO = 0.25;
const EDGE_MAX_PX = 16;
/** the cursor has to travel this much further to leave a zone than to enter it */
const HYSTERESIS_PX = 4;

/**
 * Turns the cursor position inside a row into the one instruction that both the indicator
 * and the drop use, so what is shown can never differ from what happens. Without nesting
 * (a structure with no children anywhere) the row is split in half and only reordering is
 * offered.
 */
export const dropInstructionAt = (
  offsetY: number,
  height: number,
  canNest: boolean,
  previous?: DropInstruction
): DropInstruction => {
  if (!canNest) {
    const bias = previous === 'above' ? HYSTERESIS_PX : previous === 'below' ? -HYSTERESIS_PX : 0;
    return offsetY < height / 2 + bias ? 'above' : 'below';
  }
  const edge = Math.min(height * EDGE_RATIO, EDGE_MAX_PX);
  const top = edge + (previous === 'above' ? HYSTERESIS_PX : 0);
  const bottom = height - edge - (previous === 'below' ? HYSTERESIS_PX : 0);
  if (offsetY < top) {
    return 'above';
  }
  if (offsetY > bottom) {
    return 'below';
  }
  return 'inside';
};

/** a unit cannot be dropped onto itself or onto anything it contains */
export const forbiddenDropIdsOf = (tree: WorkplaceTree, draggedId: number): Set<number> => {
  const dragged = tree.byId.get(draggedId);
  return new Set(dragged ? [draggedId, ...descendantIdsOf(dragged)] : [draggedId]);
};

const siblingsOf = (tree: WorkplaceTree, parentId: number | null): WorkplaceNode[] =>
  parentId === null ? tree.roots : tree.byId.get(parentId)?.children ?? [];

/**
 * The instruction plus the two nodes give the target parent and the slot in it. Returns null
 * for a drop that would not move anything, so no request is sent for it.
 */
export const resolveDrop = (
  tree: WorkplaceTree,
  draggedId: number,
  targetId: number,
  instruction: DropInstruction
): WorkplaceMove | null => {
  const dragged = tree.byId.get(draggedId);
  const target = tree.byId.get(targetId);
  if (!dragged || !target || forbiddenDropIdsOf(tree, draggedId).has(targetId)) {
    return null;
  }

  const parentId = instruction === 'inside' ? targetId : target.parentId;
  const siblings = siblingsOf(tree, parentId);
  // the dragged unit leaves its old slot first, so the index is counted without it
  const without = siblings.filter((sibling) => sibling.id !== draggedId);
  const index =
    instruction === 'inside'
      ? without.length
      : without.findIndex((sibling) => sibling.id === targetId) + (instruction === 'below' ? 1 : 0);

  const isSameParent = (dragged.parentId ?? null) === parentId;
  if (isSameParent && index === siblings.findIndex((sibling) => sibling.id === draggedId)) {
    return null;
  }
  return { parentId, position: index + 1 };
};
